/**
 * Paleta "Ir para…": diálogo modal com um campo (combobox) e a lista de
 * destinos (listbox com grupos), no padrão `aria-activedescendant`: o foco
 * fica no campo e as setas, Home e End movem a opção ativa; Enter vai; Esc
 * fecha (quem abriu devolve o foco). Tab não sai do diálogo.
 */
import {FC, KeyboardEvent, useEffect, useMemo, useRef, useState} from 'react';
import {createPortal} from 'react-dom';
import {useQuery} from 'react-query';
import {useLocation, useNavigate} from 'react-router-dom';

import {useAuth} from 'app/modules/auth';
import {getFeatureFlag} from 'app/requests';

import {useAppMenu} from '../aside/appMenu';
import {
  buildCommandItems,
  COMMAND_GROUP_LABELS,
  CommandGroup,
  CommandItem,
  CREATE_ACTIONS,
  filterCommandItems,
} from './commandItems';
import {readRecentPages} from './recentPages';

const LISTBOX_ID = 'opx-palette-list';
const optionId = (index: number) => `opx-palette-opcao-${index}`;

const NO_FLAGS: Record<string, boolean> = {};

/** Flags dos atalhos de criar, lidas só com a paleta aberta. Erro = desligada. */
const useCreateFlags = () => {
  const {currentUser} = useAuth();
  const uid = currentUser?.uid || '';
  const email = currentUser?.user?.email;
  const flagNames = useMemo(
    () =>
      CREATE_ACTIONS.map(({flag}) => flag).filter((flag): flag is string =>
        Boolean(flag)
      ),
    []
  );
  const query = useQuery(
    ['ir-para-flags', uid, flagNames],
    async () => {
      const entries = await Promise.all(
        flagNames.map(async (flag) => {
          try {
            const response = await getFeatureFlag(flag, uid, email);
            return [flag, Boolean(response?.flag?.value)] as const;
          } catch (error) {
            return [flag, false] as const;
          }
        })
      );
      return entries.reduce<Record<string, boolean>>(
        (flags, [flag, value]) => ({...flags, [flag]: value}),
        {}
      );
    },
    {enabled: Boolean(uid), staleTime: 5 * 60 * 1000, retry: false}
  );
  return query.data || NO_FLAGS;
};

type Props = {
  onClose: () => void;
};

const PaletteDialog: FC<Props> = ({onClose}) => {
  const navigate = useNavigate();
  const {pathname} = useLocation();
  const {currentUser} = useAuth();
  const menu = useAppMenu();
  const flags = useCreateFlags();
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const userUid = currentUser?.uid || '';

  const recents = useMemo(
    () => (userUid ? readRecentPages(userUid) : []),
    [userUid]
  );
  const items = useMemo(
    () => buildCommandItems({menu, recents, currentPath: pathname, flags}),
    [menu, recents, pathname, flags]
  );
  const visible = useMemo(
    () => filterCommandItems(items, query),
    [items, query]
  );

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  // Lista nova (busca ou atalho que chegou com a flag): volta para a primeira
  useEffect(() => {
    setActive(0);
  }, [visible]);

  // A opção ativa sempre à vista quando a lista rola
  useEffect(() => {
    document
      .getElementById(optionId(active))
      ?.scrollIntoView?.({block: 'nearest'});
  }, [active]);

  const go = (item: CommandItem) => {
    onClose();
    navigate(item.to);
  };

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    const last = visible.length - 1;
    const move = (index: number) => {
      event.preventDefault();
      if (last < 0) return;
      setActive(index);
    };
    switch (event.key) {
      case 'ArrowDown':
        move(active >= last ? 0 : active + 1);
        break;
      case 'ArrowUp':
        move(active <= 0 ? last : active - 1);
        break;
      case 'Home':
        move(0);
        break;
      case 'End':
        move(last);
        break;
      case 'Enter':
        event.preventDefault();
        if (visible[active]) go(visible[active]);
        break;
      case 'Escape':
        event.preventDefault();
        event.stopPropagation();
        onClose();
        break;
      case 'Tab':
        // Um campo só: o foco fica nele
        event.preventDefault();
        inputRef.current?.focus();
        break;
      default:
    }
  };

  const groups = visible.reduce<{group: CommandGroup; entries: number[]}[]>(
    (acc, item, index) => {
      const current = acc[acc.length - 1];
      if (current && current.group === item.group) current.entries.push(index);
      else acc.push({group: item.group, entries: [index]});
      return acc;
    },
    []
  );

  return (
    <div className='opx-app opx-palette-root'>
      <div
        className='opx-palette-backdrop'
        data-testid='ir-para-fundo'
        onMouseDown={onClose}
      />
      <div
        className='opx-palette'
        role='dialog'
        aria-modal='true'
        aria-label='Ir para'
        onKeyDown={onKeyDown}
        // Clique fora do campo não tira o foco dele
        onMouseDown={(event) => {
          if (event.target !== inputRef.current) event.preventDefault();
        }}
      >
        <div className='opx-palette__field'>
          <i className='bi bi-search' aria-hidden='true' />
          <input
            ref={inputRef}
            type='text'
            role='combobox'
            aria-expanded='true'
            aria-controls={LISTBOX_ID}
            aria-autocomplete='list'
            aria-activedescendant={
              visible.length ? optionId(active) : undefined
            }
            aria-label='Ir para'
            placeholder='Ir para…'
            autoComplete='off'
            spellCheck={false}
            value={query}
            onChange={(event) => setQuery(event.target.value)}
          />
          <kbd className='opx-kbd'>Esc</kbd>
        </div>
        <div
          id={LISTBOX_ID}
          className='opx-palette__list'
          role='listbox'
          aria-label='Destinos'
        >
          {groups.map(({group, entries}) => (
            <div
              key={group}
              role='group'
              aria-labelledby={`opx-palette-grupo-${group}`}
              className='opx-palette__group'
            >
              <div
                id={`opx-palette-grupo-${group}`}
                className='opx-palette__grouplabel'
                role='presentation'
              >
                {COMMAND_GROUP_LABELS[group]}
              </div>
              {entries.map((index) => {
                const item = visible[index];
                return (
                  <div
                    key={item.id}
                    id={optionId(index)}
                    role='option'
                    aria-selected={index === active}
                    className={`opx-palette__option${
                      index === active ? ' is-active' : ''
                    }`}
                    onMouseMove={() => index !== active && setActive(index)}
                    // O foco fica no campo
                    onMouseDown={(event) => event.preventDefault()}
                    onClick={() => go(item)}
                  >
                    <i className={`bi ${item.icon}`} aria-hidden='true' />
                    <span className='opx-palette__title' title={item.title}>
                      {item.title}
                    </span>
                    {item.context && (
                      <span className='opx-palette__context'>
                        {item.context}
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          ))}
        </div>
        {!visible.length && (
          <p className='opx-palette__empty' role='status'>
            Nada encontrado
          </p>
        )}
      </div>
    </div>
  );
};

const CommandPalette: FC<Props & {open: boolean}> = ({open, onClose}) =>
  open
    ? createPortal(<PaletteDialog onClose={onClose} />, document.body)
    : null;

export default CommandPalette;
